import { useRef } from 'react';
import { formatDateToLocal } from '@safetyhub/shared';
import { Button, Card, Modal, TextButton, buttonClassName } from '@safetyhub/ui';
import { AlertTriangleIcon, ChevronRightIcon } from '../../icons';
import { MODAL_LAYER } from '../../modalLayers';

/*
 * 이 파일은 갈래가 둘이다 — 중앙 다이얼로그 셋(`InspectionStartDialog`·`RestoreConfirmDialog`·
 * `DeleteConfirmDialog`)은 base `Modal`이고, 바텀시트 셋(`…BottomSheet`)은 base에 시트 갈래가
 * 없어 raw 오버레이로 남는다. 그래서 면제를 **파일 단위**(`RAW_OVERLAY_EXEMPT`)가 아니라 시트의
 * **줄 단위** `eslint-disable-next-line`으로 둔다 — 파일째 면제하면 이 파일에 새로 생기는 중앙
 * 모달까지 감시 밖으로 나간다(그 docblock의 「섞인 파일」 경고).
 */

interface InProgressInspection {
  id: string;
  siteId: string;
  siteName: string;
  inspectorId: string;
  inspectorName: string;
  status: string;
  photoCount: number;
  findingCount: number;
  contributorCount: number;
  startedAt: string;
}

interface InspectionStartDialogProps {
  /** 다이얼로그 표시 여부 */
  isOpen: boolean;
  /** 닫기 핸들러 */
  onClose: () => void;
  /** 현재 사용자 ID */
  currentUserId: string;
  /** 현재 현장 이름 */
  siteName: string;
  /** 현재 현장의 진행 중 점검 목록 */
  inProgressInspections: InProgressInspection[];
  /** 새 점검 시작 */
  onStartNew: () => void;
  /** 기존 점검 이어서 (내 점검) */
  onContinue: (inspectionId: string) => void;
  /** 로딩 상태 */
  isLoading?: boolean;
}

export function InspectionStartDialog({
  isOpen,
  onClose,
  currentUserId,
  siteName,
  inProgressInspections,
  onStartNew,
  onContinue,
  isLoading = false,
}: InspectionStartDialogProps) {
  if (!isOpen) return null;

  // 내 진행 중 점검만 분류 (타인 점검은 A-③ 정책에 따라 제거)
  const myInspection = inProgressInspections.find(
    (i) => i.inspectorId === currentUserId
  );

  const hasMyInspection = !!myInspection;

  // 처리 중에는 닫기 수단을 전부 막는다(종전엔 취소만 disabled이고 바깥 클릭으로 닫혔다).
  return (
    <Modal
      open
      onClose={onClose}
      title="점검 시작"
      size="md"
      overlayZClassName={MODAL_LAYER.root}
      closeLocked={isLoading}
      footer={
        <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
          취소
        </Button>
      }
    >
        {/* 본문 */}
        <div className="space-y-6">
          <p className="text-sm text-ink-500">{siteName}</p>

          {/* 진행 중 점검 안내 */}
          {hasMyInspection && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-card p-4">
              <p className="text-sm text-yellow-800">
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <AlertTriangleIcon className="h-4 w-4" />진행 중인 점검이 있습니다
                </span>
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                이전에 시작한 점검이 아직 완료되지 않았습니다.
              </p>
            </div>
          )}

          {/* 내 진행 중 점검 */}
          {myInspection && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-ink-700">내 진행 중 점검</h3>
              <InspectionSummaryCard
                inspection={myInspection}
                onAction={() => onContinue(myInspection.id)}
                actionLabel="이어서 점검"
                isLoading={isLoading}
              />
            </div>
          )}

          {/* 새 점검 시작 옵션 */}
          <div className="pt-4 border-t border-line-soft">
            <button
              type="button"
              onClick={onStartNew}
              disabled={isLoading}
              className="w-full py-3 px-4 text-ink-600 bg-surface-hover hover:bg-ink-200 rounded-card-sm font-medium transition-colors disabled:opacity-50"
            >
              {isLoading ? '처리 중...' : '새 점검 시작'}
            </button>
            <p className="text-xs text-ink-400 mt-2 text-center">
              진행 중인 점검과 별개로 새로운 점검을 시작합니다
            </p>
          </div>
        </div>
    </Modal>
  );
}

/**
 * 진행 중 점검 요약 카드
 */
function InspectionSummaryCard({
  inspection,
  onAction,
  actionLabel,
  isLoading,
}: {
  inspection: InProgressInspection;
  onAction: () => void;
  actionLabel: string;
  isLoading: boolean;
}) {
  const startedAt = new Date(inspection.startedAt);
  const timeAgo = getTimeAgo(startedAt);

  return (
    <Card elevation="none">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* 시작자 정보 */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium text-ink-800">
              {inspection.inspectorName}
            </span>
            {inspection.contributorCount > 1 && (
              <span className="text-xs text-ink-500">
                + {inspection.contributorCount - 1}명 참여
              </span>
            )}
          </div>

          {/* 진행 상황 */}
          <div className="flex items-center gap-4 text-xs text-ink-500">
            <span>사진 {inspection.photoCount}장</span>
            <span className="inline-flex items-center gap-1">
              <AlertTriangleIcon className="h-3 w-3" />
              {inspection.findingCount}건 발견
            </span>
            <span>{timeAgo}</span>
          </div>
        </div>

        {/* 액션 버튼 */}
        <Button
          type="button"
          variant="primary"
          className="whitespace-nowrap"
          onClick={onAction}
          disabled={isLoading}
        >
          {actionLabel}
        </Button>
      </div>
    </Card>
  );
}

/**
 * 상대 시간 표시 (예: "30분 전")
 */
function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);

  if (diffMins < 1) return '방금 전';
  if (diffMins < 60) return `${diffMins}분 전`;
  if (diffHours < 24) return `${diffHours}시간 전`;
  return formatDateToLocal(date);
}

/**
 * 모바일용 바텀시트 버전
 */
export function InspectionStartBottomSheet({
  isOpen,
  onClose,
  currentUserId,
  siteName,
  inProgressInspections,
  onStartNew,
  onContinue,
  isLoading = false,
}: InspectionStartDialogProps) {
  if (!isOpen) return null;

  const myInspection = inProgressInspections.find(
    (i) => i.inspectorId === currentUserId
  );

  return (
    // eslint-disable-next-line local/no-raw-overlay -- 바텀시트 갈래(base에 시트가 없다 — RAW_OVERLAY_EXEMPT docblock의 NOT_A_MODAL 판정)
    <div className="fixed inset-0 z-[70]">
      {/* 백드롭 */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* 바텀시트 */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl overflow-hidden animate-slide-up">
        {/* 핸들 */}
        <div className="flex justify-center py-3">
          <div className="w-12 h-1 bg-ink-300 rounded-full" />
        </div>

        {/* 헤더 */}
        <div className="px-5 pb-4">
          <h2 className="text-lg font-semibold text-ink-900">점검 시작</h2>
          <p className="text-sm text-ink-500">{siteName}</p>
        </div>

        {/* 옵션 버튼들 */}
        <div className="px-5 pb-8 space-y-3">
          {/* 내 점검 이어서 */}
          {myInspection && (
            // Button 갈래가 아니다 — 제목+부제 2줄과 오른쪽 화살표를 좌우로 벌린 **선택 카드**형이라
            // (text-left, justify-between, py-4=56px) Button의 중앙정렬·고정높이와 맞지 않는다.
            // className으로 전부 덮으면 프리미티브를 쓰는 의미가 없어진다 → SelectableCard 후보.
            // eslint-disable-next-line local/no-self-painted-button
            <button
              type="button"
              onClick={() => onContinue(myInspection.id)}
              disabled={isLoading}
              className="w-full py-4 px-4 bg-brand-600 text-white rounded-card font-medium text-left flex items-center justify-between"
            >
              <div>
                <p>이어서 점검</p>
                <p className="text-xs text-brand-200 mt-0.5">
                  사진 {myInspection.photoCount}장 · {myInspection.findingCount}건 발견
                </p>
              </div>
              <ChevronRightIcon className="h-5 w-5 flex-none" />
            </button>
          )}

          {/* 새 점검 시작 */}
          <button
            type="button"
            onClick={onStartNew}
            disabled={isLoading}
            className="w-full py-4 px-4 bg-surface-hover text-ink-700 rounded-card font-medium text-left flex items-center justify-between"
          >
            <div>
              <p>새 점검 시작</p>
              <p className="text-xs text-ink-500 mt-0.5">
                별도의 새 점검을 시작합니다
              </p>
            </div>
            <ChevronRightIcon className="h-5 w-5 flex-none text-ink-400" />
          </button>

          {/* 취소 */}
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="w-full py-3 text-ink-500 font-medium"
          >
            취소
          </button>
        </div>
      </div>
    </div>
  );
}

export default InspectionStartDialog;

/**
 * 삭제된 점검 복원 확인 다이얼로그
 */
interface RestoreConfirmDialogProps {
  /** 다이얼로그 표시 여부 */
  isOpen: boolean;
  /** 닫기 핸들러 */
  onClose: () => void;
  /** 복원 핸들러 */
  onRestore: () => void;
  /** 삭제된 점검 정보 */
  inspection: {
    siteName: string;
    inspectionDate: string;
    inspectorName: string;
  } | null;
  /** 로딩 상태 */
  isLoading?: boolean;
  /** 복원 권한 여부 */
  canRestore?: boolean;
}

export function RestoreConfirmDialog({
  isOpen,
  onClose,
  onRestore,
  inspection,
  isLoading = false,
  canRestore = true,
}: RestoreConfirmDialogProps) {
  if (!isOpen || !inspection) return null;

  const formattedDate = new Date(inspection.inspectionDate).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // 복원 중에는 닫기 수단을 전부 막는다(종전엔 버튼만 disabled이고 바깥 클릭으로 닫혔다).
  return (
    <Modal
      open
      onClose={onClose}
      title="삭제된 점검"
      size="sm"
      overlayZClassName={MODAL_LAYER.root}
      closeLocked={isLoading}
      footer={
        canRestore ? (
          <>
            <Button type="button" className="flex-1" onClick={onClose} disabled={isLoading}>
              취소
            </Button>
            <Button
              type="button"
              variant="primary"
              className="flex-1"
              onClick={onRestore}
              disabled={isLoading}
            >
              {isLoading ? '복원 중...' : '복원'}
            </Button>
          </>
        ) : (
          <Button type="button" className="flex-1" onClick={onClose}>
            닫기
          </Button>
        )
      }
    >
          {/* 점검 정보 */}
          <div className="bg-surface-soft rounded-card p-4 mb-4">
            <p className="font-medium text-ink-900">{inspection.siteName}</p>
            <p className="text-sm text-ink-500 mt-1">
              {formattedDate} · {inspection.inspectorName}
            </p>
          </div>

          {canRestore ? (
            <p className="text-sm text-ink-600">
              이 점검은 삭제되었습니다. 복원하시겠습니까?
            </p>
          ) : (
            <p className="text-sm text-ink-600">
              이 점검은 삭제되었습니다.<br />
              <span className="text-red-600">점검 생성자 또는 조직 관리자만 복원할 수 있습니다.</span>
            </p>
          )}
    </Modal>
  );
}

/**
 * 모바일용 복원 확인 바텀시트
 */
export function RestoreConfirmBottomSheet({
  isOpen,
  onClose,
  onRestore,
  inspection,
  isLoading = false,
  canRestore = true,
}: RestoreConfirmDialogProps) {
  if (!isOpen || !inspection) return null;

  const formattedDate = new Date(inspection.inspectionDate).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    // eslint-disable-next-line local/no-raw-overlay -- 바텀시트 갈래(base에 시트가 없다 — RAW_OVERLAY_EXEMPT docblock의 NOT_A_MODAL 판정)
    <div className="fixed inset-0 z-[70]">
      {/* 백드롭 */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* 바텀시트 */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl overflow-hidden animate-slide-up">
        {/* 핸들 */}
        <div className="flex justify-center py-3">
          <div className="w-12 h-1 bg-ink-300 rounded-full" />
        </div>

        {/* 헤더 */}
        <div className="px-5 pb-4">
          <h2 className="text-lg font-semibold text-ink-900">삭제된 점검</h2>
        </div>

        {/* 점검 정보 */}
        <div className="px-5 pb-4">
          <div className="bg-surface-soft rounded-card p-4">
            <p className="font-medium text-ink-900">{inspection.siteName}</p>
            <p className="text-sm text-ink-500 mt-1">
              {formattedDate} · {inspection.inspectorName}
            </p>
          </div>
        </div>

        {/* 버튼들 */}
        <div className="px-5 pb-8 space-y-3">
          {canRestore ? (
            <>
              <p className="text-sm text-ink-600 text-center mb-2">
                이 점검을 복원하시겠습니까?
              </p>
              <Button
                type="button"
                variant="primary"
                size="lg"
                className="w-full"
                onClick={onRestore}
                disabled={isLoading}
              >
                {isLoading ? '복원 중...' : '복원'}
              </Button>
              <TextButton
                type="button"
                tone="neutral"
                onClick={onClose}
                disabled={isLoading}
                className="w-full py-3 font-medium"
              >
                취소
              </TextButton>
            </>
          ) : (
            <>
              <p className="text-sm text-center text-red-600 mb-2">
                점검 생성자 또는 조직 관리자만 복원할 수 있습니다.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="w-full py-3 text-ink-500 font-medium"
              >
                닫기
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * 점검 삭제 확인 다이얼로그
 */
interface DeleteConfirmDialogProps {
  /** 다이얼로그 표시 여부 */
  isOpen: boolean;
  /** 닫기 핸들러 */
  onClose: () => void;
  /** 삭제 핸들러 */
  onDelete: () => void;
  /** 점검 정보 */
  inspection: {
    siteName: string;
    inspectionDate: string;
    inspectorName: string;
  } | null;
  /** 로딩 상태 */
  isLoading?: boolean;
}

export function DeleteConfirmDialog({
  isOpen,
  onClose,
  onDelete,
  inspection,
  isLoading = false,
}: DeleteConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  if (!isOpen || !inspection) return null;

  const formattedDate = new Date(inspection.inspectionDate).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // danger 규약 — 첫 포커스는 「취소」(Enter 오조작으로 삭제가 나가지 않게). `Button`은 ref를 넘기지
  // 않아(React 18, forwardRef 없음) 취소만 같은 정본 클래스의 네이티브 버튼으로 둔다.
  // 삭제 중에는 닫기 수단을 전부 막는다.
  return (
    <Modal
      open
      onClose={onClose}
      title="점검 삭제"
      size="sm"
      overlayZClassName={MODAL_LAYER.root}
      initialFocusRef={cancelRef}
      closeLocked={isLoading}
      footer={
        <>
          <button
            ref={cancelRef}
            type="button"
            className={buttonClassName('secondary', 'md', 'flex-1')}
            onClick={onClose}
            disabled={isLoading}
          >
            취소
          </button>
          <Button
            type="button"
            variant="danger"
            className="flex-1"
            onClick={onDelete}
            disabled={isLoading}
          >
            {isLoading ? '삭제 중...' : '삭제'}
          </Button>
        </>
      }
    >
          {/* 점검 정보 */}
          <div className="bg-surface-soft rounded-card p-4 mb-4">
            <p className="font-medium text-ink-900">{inspection.siteName}</p>
            <p className="text-sm text-ink-500 mt-1">
              {formattedDate} · {inspection.inspectorName}
            </p>
          </div>

          <p className="text-sm text-ink-600 mb-2">
            이 점검을 삭제하시겠습니까?
          </p>

          {/* 경고 문구 */}
          <div className="bg-amber-50 border border-amber-200 rounded-card p-3">
            <p className="text-sm text-amber-800">
              <span className="font-medium">주의:</span> 삭제된 점검은 30일 이내에 복원할 수 있습니다. 30일이 지나면 영구적으로 삭제되어 복구할 수 없습니다.
            </p>
          </div>
    </Modal>
  );
}

/**
 * 모바일용 삭제 확인 바텀시트
 */
export function DeleteConfirmBottomSheet({
  isOpen,
  onClose,
  onDelete,
  inspection,
  isLoading = false,
}: DeleteConfirmDialogProps) {
  if (!isOpen || !inspection) return null;

  const formattedDate = new Date(inspection.inspectionDate).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    // eslint-disable-next-line local/no-raw-overlay -- 바텀시트 갈래(base에 시트가 없다 — RAW_OVERLAY_EXEMPT docblock의 NOT_A_MODAL 판정)
    <div className="fixed inset-0 z-[70]">
      {/* 백드롭 */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* 바텀시트 */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl overflow-hidden animate-slide-up">
        {/* 핸들 */}
        <div className="flex justify-center py-3">
          <div className="w-12 h-1 bg-ink-300 rounded-full" />
        </div>

        {/* 헤더 */}
        <div className="px-5 pb-4">
          <h2 className="text-lg font-semibold text-red-900">점검 삭제</h2>
        </div>

        {/* 점검 정보 */}
        <div className="px-5 pb-4">
          <div className="bg-surface-soft rounded-card p-4">
            <p className="font-medium text-ink-900">{inspection.siteName}</p>
            <p className="text-sm text-ink-500 mt-1">
              {formattedDate} · {inspection.inspectorName}
            </p>
          </div>
        </div>

        {/* 경고 문구 */}
        <div className="px-5 pb-4">
          <div className="bg-amber-50 border border-amber-200 rounded-card p-3">
            <p className="text-sm text-amber-800">
              <span className="font-medium">주의:</span> 삭제된 점검은 30일 이내에 복원할 수 있습니다. 30일이 지나면 영구적으로 삭제되어 복구할 수 없습니다.
            </p>
          </div>
        </div>

        {/* 버튼들 */}
        <div className="px-5 pb-8 space-y-3">
          <Button
            type="button"
            variant="danger"
            size="lg"
            className="w-full"
            onClick={onDelete}
            disabled={isLoading}
          >
            {isLoading ? '삭제 중...' : '삭제'}
          </Button>
          <TextButton
            type="button"
            tone="neutral"
            onClick={onClose}
            disabled={isLoading}
            className="w-full py-3 font-medium"
          >
            취소
          </TextButton>
        </div>
      </div>
    </div>
  );
}
